import type { CapabilitiesSnapshot, MeResponse } from '../../entities/me/me-contract.types';
import { PERM_ORGANIZATION_SETTINGS_VIEW } from '../../features/org-settings/model/org-settings.permissions';
import { PERM_BRANCHES_ORG_WRITE } from '../../features/organization-branches/model/branches.permissions';
import { hasPermission, isJwtPlatformSuperadminSession, isRouteAllowed } from './access';
import type { StaffSPARoutePath } from './staff-spa-route-paths';

/**
 * JWT СА в org-context (`authenticated_with_org_context` / `impersonating_org`):
 * tenant-wide экраны не режутся overlay АФ (`branches:profile_write` без `org_write`) — уровень UI не ниже АТ.
 * Не подменяет `routes_allowed` / модули; только снимает дополнительный фронтовый контур относительно `branches:org_write`.
 */
export type SuperadminOrgTenantWideUiGateContext = {
  me: Pick<MeResponse, 'role'> | null | undefined;
  orgBootstrap: boolean;
};

export function isSuperadminJwtTenantOrgWideUiGateLift(ctx: SuperadminOrgTenantWideUiGateContext | null | undefined): boolean {
  if (!ctx?.orgBootstrap) return false;
  return isJwtPlatformSuperadminSession(ctx.me);
}

/**
 * Управление сетью филиалов на уровне тенанта (`branches:org_write`): создание точек, деактивация,
 * а также согласованные с матрицей АФ/АТ пункты «governance» в staff SPA (RBAC, шлюзы, аудит платежей тенанта).
 * Профиль-only (`branches:profile_write` без org_write) — операционный контур филиала без этих экранов.
 */
export function hasTenantStaffBranchesOrgGovernanceContour(
  capabilities: CapabilitiesSnapshot | null | undefined
): boolean {
  return hasPermission(capabilities, PERM_BRANCHES_ORG_WRITE);
}

/** Контур `branches:org_write` или подъём для JWT СА в org-context (см. `SuperadminOrgTenantWideUiGateContext`). */
export function hasTenantWideStaffGovernanceContourOrSaOrgLift(
  capabilities: CapabilitiesSnapshot | null | undefined,
  ctx?: SuperadminOrgTenantWideUiGateContext | null
): boolean {
  if (hasTenantStaffBranchesOrgGovernanceContour(capabilities)) return true;
  return isSuperadminJwtTenantOrgWideUiGateLift(ctx);
}

const TENANT_GOVERNANCE_STAFF_ROUTE_KEYS = new Set<StaffSPARoutePath>([
  '/access-rbac',
  '/payment-gateways',
  '/audit-payments'
]);

export function isTenantStaffGovernanceKeyedRoute(backendKey: StaffSPARoutePath): boolean {
  return TENANT_GOVERNANCE_STAFF_ROUTE_KEYS.has(backendKey);
}

/** Операционный реестр/контур платежей тенанта (не шлюзы и не tenant-wide аудит в governance-наборе). */
export function isTenantStaffOperationalPaymentsKeyedRoute(backendKey: StaffSPARoutePath): boolean {
  return backendKey === '/payments';
}

/**
 * Карточка tenant-организации и legacy-ключ маршрута (`/organization`, `/organization/settings` → единый surface):
 * при `organization:settings:view` и разрешённом маршруте — контур `branches:org_write` (как у АТ vs overlay АФ).
 * JWT СА в org-context: без `branches:org_write` не деградировать ниже АТ (см. `SuperadminOrgTenantWideUiGateContext`).
 * Не путать с `branches:profile_write` (АФ) — тот же смысл «tenant-wide», что у `BranchesTenantCreateAllowed`
 * в `backend/core/access/branch_tenant.go`.
 */
export function isTenantOrganizationProfileStaffRouteKey(
  key: StaffSPARoutePath
): key is '/organization' | '/organization/settings' {
  return key === '/organization' || key === '/organization/settings';
}

export function canAccessTenantOrganizationProfileSurface(
  capabilities: CapabilitiesSnapshot | null | undefined,
  backendRouteKey: '/organization' | '/organization/settings',
  ctx?: SuperadminOrgTenantWideUiGateContext | null
): boolean {
  if (!isRouteAllowed(capabilities, backendRouteKey)) return false;
  if (!hasPermission(capabilities, PERM_ORGANIZATION_SETTINGS_VIEW)) return false;
  return hasTenantWideStaffGovernanceContourOrSaOrgLift(capabilities, ctx);
}

/**
 * Единый org-profile surface на `/organization`: допуск по любому из ключей `/organization` или `/organization/settings`
 * в `routes_allowed` при том же гейте (legacy deep link и снимки только с одним из ключей).
 */
export function canAccessTenantOrganizationUnifiedProfileSurface(
  capabilities: CapabilitiesSnapshot | null | undefined,
  ctx?: SuperadminOrgTenantWideUiGateContext | null
): boolean {
  return (
    canAccessTenantOrganizationProfileSurface(capabilities, '/organization', ctx) ||
    canAccessTenantOrganizationProfileSurface(capabilities, '/organization/settings', ctx)
  );
}
